/* eslint-disable max-lines-per-function */
import React from 'react'
import {
  getCrossRefPapers,
  getCrossRefPaperByDoi,
  CrossRefPaper,
  getSearchTypeLabel
} from '@cpsy-rmap-project/web-core/crossref'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'
import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useEffectOnce } from 'react-use'
import styled from 'styled-components'
import { getAutoTags } from '../../../utils/autoTags'
import { getAbstract } from '../../../utils/semanticScholar'
import ExternalLink from '../../atoms/ExternalLink'
import { useLoading } from '../../atoms/Loading'
import { usePaperDetailsModal } from '../../molecules/PaperDetailsModal/hooks'
import { usePaper } from '../../organisms/Paper'
import { useSubmitModelDialog } from './hooks'
import { varidateKeywords } from './varidation'

const Root = styled(Box)`
  width: 100%;
  height: 100%;
`
const Centering = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ListContent = styled(Box)`
  height: 100%;
  /* overflow: scroll; */
`

interface Props {}

interface SearchPaperError {
  message: string
}

const getSemanticScholarAbstract = async ({ doi, abstract }: { doi: string; abstract: string }) => {
  // アブスト取得（CrossRefAPIで取れなかった場合SemanticScholarAPIで取得する）
  if (!_.isEmpty(doi) && _.isEmpty(abstract)) {
    const semanticScholarAbstract = await getAbstract({ doi })
    return { abstract: semanticScholarAbstract }
  }
  return { abstract }
}

const getTagsAutomatically = async ({ title, abstract }: { title: string; abstract: string }) => {
  // タグ自動付与設定
  // NOTE: 必要に応じてsrc/utils/autoTagsのgetAutoTagsを改造して下さい。
  const autoTags = await getAutoTags(`${title} ${abstract}`)
  return autoTags
}

const Component: React.FC<Props> = () => {
  const snackbar = useSnackbar()
  const allPapers = usePaper().papers
  const { handleIsLoading } = useLoading()
  const { handlePaperDetailsModalOpen } = usePaperDetailsModal()
  const { dialog, handle } = useSubmitModelDialog()
  const { keywords, isUseDoi } = dialog
  const { handleSelectPaper, handleDetailsChange, handleResetPaper, handleNext, handleBack, handleIsReadySearch } =
    handle

  const [papers, setPapers] = React.useState<CrossRefPaper[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [errors, setErrors] = React.useState<SearchPaperError[]>([])

  const selectPaper = async (_crossRefPaper: CrossRefPaper) => {
    handleSelectPaper(_crossRefPaper)

    handleIsLoading(true)
    handleNext()
    try {
      const updateSemanticScholarAbstract = await getSemanticScholarAbstract({
        doi: _crossRefPaper.doi,
        abstract: _crossRefPaper.abstract
      })

      const updateTagsAutomatically = await getTagsAutomatically({
        title: _crossRefPaper.title,
        abstract: updateSemanticScholarAbstract.abstract
      })

      handleResetPaper()

      handleDetailsChange({
        ..._crossRefPaper,
        ...updateSemanticScholarAbstract,
        ...updateTagsAutomatically
      })

      handleIsLoading(false)
    } catch (e) {
      console.error(e)
    }
  }

  useEffectOnce(() => {
    const getPapers = async () => {
      setLoading(true)
      try {
        if (isUseDoi) {
          setPapers(await getCrossRefPaperByDoi({ doi: keywords }))
        } else {
          setPapers(await getCrossRefPapers({ term: encodeURIComponent(keywords) }))
        }
      } catch (e) {
        console.error(e)
        setErrors([{ message: '論文検索に失敗しました' }])
      } finally {
        setLoading(false)
      }
    }

    const varidateErrors = varidateKeywords(keywords)
    if (!_.isEmpty(varidateErrors)) {
      varidateErrors.forEach(error => {
        snackbar.enqueueSnackbar(error, { variant: 'error' })
      })
      handleBack()
      handleIsReadySearch(false)
    } else {
      getPapers()
    }
  })

  const HandleButtonArea: React.FC = () => (
    <Box style={{ textAlign: 'center', marginTop: '20px' }}>
      <Box style={{ margin: '10px' }}>
        <Button variant="text" color="default" size="small" onClick={handleBack}>
          Back
        </Button>
      </Box>
    </Box>
  )

  if (loading) {
    return (
      <Root>
        <Centering>
          <Box>
            <Box style={{ textAlign: 'center' }}>
              <CircularProgress size={100} thickness={1} />
              <Typography>Searching papers...</Typography>
              <Typography>
                &quot;
                {keywords}
                &quot;
              </Typography>
            </Box>
            <Box>
              <ul>
                <Typography variant="body2">
                  <li>It takes 3 ~ 10 seconds.</li>
                  <li>
                    If it takes too long or wrong search result, please enter more detailed keywords (ex. Paper Title
                    Author Year).
                  </li>
                  <li>If you know DOI, searching in DOI mode is accurate.</li>
                </Typography>
              </ul>
            </Box>
          </Box>
        </Centering>
        <HandleButtonArea />
      </Root>
    )
  }

  if (errors.length !== 0) {
    return (
      <Root>
        <Centering>
          <Box>
            <Box style={{ textAlign: 'center' }}>
              <Typography variant="h5">Oops! something went wrong.</Typography>
              <Typography>Please back and try again.</Typography>
            </Box>
            <Box>
              <ul>
                <Typography variant="body2">
                  <li>Could not be obtained due to network effects.</li>
                </Typography>
                <Typography variant="body2">
                  <li>Please try again later.</li>
                </Typography>
                <Typography variant="body2">
                  <li>If it does not work, please contact us.</li>
                </Typography>
              </ul>
            </Box>
          </Box>
        </Centering>
        <HandleButtonArea />
      </Root>
    )
  }

  return (
    <Root>
      <Box>
        <Typography>Select a paper.</Typography>
      </Box>
      <ListContent>
        <List>
          {papers.map(crossRefPaper => {
            const samePaper = allPapers.find(p => p.doi === crossRefPaper.doi)
            const isExistsSamePaper = !!samePaper
            return (
              <ListItem
                button
                key={crossRefPaper.doi}
                onClick={() => {
                  if (isExistsSamePaper) {
                    handlePaperDetailsModalOpen(samePaper)
                  } else {
                    selectPaper(crossRefPaper)
                  }
                }}
                // disabled={isExistsSamePaper}
                divider
              >
                <ListItemText
                  primary={
                    <Typography component="span" color="textPrimary">
                      {_.truncate(crossRefPaper.title, {
                        length: 150 /* 最大150字 */
                      })}{' '}
                      <ExternalLink url={crossRefPaper.url} />
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="textPrimary">
                        {_.truncate(crossRefPaper.authors, {
                          length: 100 /* 最大100字 */
                        })}
                        , {crossRefPaper.year}
                      </Typography>
                      <br />
                      <Typography component="span" variant="caption" color="textPrimary">
                        {crossRefPaper.journal}
                        <br />
                        Citation: {crossRefPaper.citedCount}
                        <br />
                        Type: {getSearchTypeLabel(crossRefPaper.type)}
                      </Typography>
                      <br />
                      {isExistsSamePaper && (
                        <>
                          <Typography variant="body2" color="error" component="span">
                            This item has already been added.
                          </Typography>
                          <Button size="small" variant="outlined" style={{ marginLeft: '10px' }}>
                            View Details
                          </Button>
                        </>
                      )}
                    </>
                  }
                />
              </ListItem>
            )
          })}
        </List>
      </ListContent>
      <Box style={{ margin: '10px 0', textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          If you cannot find the paper you want, please back and enter more detailed keywords (ex. Paper Title Author
          Year). If you know DOI, searching in DOI mode is accurate.
        </Typography>
      </Box>
      <HandleButtonArea />
    </Root>
  )
}

export default Component
